import React, { useState, useEffect, useRef } from "react";
import {
  handleUserSignUp,
  handleResendEmailVerificationLink,
  handleChefSignUp,
} from "../../auth/Auth";
import { handleGetCitites, handleGetAreas } from "../../../services/region";
import MapWithConditionalRendering from "../Map/MapWithConditionRendering";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SearchableSelect from "../SearchableSelect";

const HeroBanner = () => {
  const [activeTab, setActiveTab] = useState("chef"); // "user" or "chef"
  const [showPassword, setShowPassword] = useState(false);
  const [showChefPassword, setShowChefPassword] = useState(false);

  // User form states
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
    phone: "",
    first_name: "",
    last_name: "",
  });

  // Chef form states - similar to TopBanner but without location for initial signup
  const [chefFormData, setChefFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
    profile_pic: "",
    cover_pic: "",
    is_chef: 1,
    nic_number: "",
    nic_image: "",
    latest_bill: "",
  });

  // Chef image states
  const [isProfile, setIsProfile] = useState("");
  const [isCover, setIsCover] = useState("");
  const [isNicImage, setIsNicImage] = useState("");
  const [isLatestBill, setIsLatestBill] = useState("");

  // Chef form refs
  const chefFileInputRef = useRef(null);
  const chefProfileDiv = useRef(null);
  const chefCoverImageRef = useRef(null);
  const chefNicImageRef = useRef(null);
  const chefLatestBillRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [chefLoading, setChefLoading] = useState(false);

  // Separate error states for each form
  const [errors, setErrors] = useState({
    email: "",
    password: "",
    phone: "",
    first_name: "",
    last_name: "",
  });

  const [chefErrors, setChefErrors] = useState({
    email: "",
    password: "",
    phone: "",
    first_name: "",
    last_name: "",
    profile_pic: "",
    cover_pic: "",
    nic_number: "",
  });

  const [touched, setTouched] = useState({
    email: false,
    password: false,
    phone: false,
    first_name: false,
    last_name: false,
  });

  // Chef terms checkboxes
  const [chefTermsAgreed, setChefTermsAgreed] = useState(false);
  const [chefAcknowledgementAgreed, setChefAcknowledgementAgreed] =
    useState(false);

  // 🔹 New states for resend link
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendEmail, setResendEmail] = useState("");

  // City/Area/Address states for chef form
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [streetAddress, setStreetAddress] = useState("");

  // Map / location states for chef form
  const [chefCoordinates, setChefCoordinates] = useState({ lat: "", lng: "" });
  const [isChefLocationConfirmed, setIsChefLocationConfirmed] = useState(false);

  const navigate = useNavigate();

  // Fetch cities on mount
  useEffect(() => {
    const fetchCities = async () => {
      const data = await handleGetCitites();
      if (data) setCities(data);
    };
    fetchCities();
  }, []);

  // Fetch areas when city changes
  useEffect(() => {
    const fetchAreas = async () => {
      if (!selectedCity) {
        setAreas([]);
        setSelectedArea("");
        return;
      }
      const city = cities.find((c) => c.name === selectedCity);
      if (city) {
        const data = await handleGetAreas(city.id);
        if (data) setAreas(data);
      }
    };
    fetchAreas();
  }, [selectedCity, cities]);

  // Validation functions
  const validateName = (name) => /^[A-Za-z]{1,20}$/.test(name);
  const validatePassword = (password) => /^[^\s]{8,30}$/.test(password);
  const validatePhone = (phone) => /^03\d{9}$/.test(phone);
  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Common validation for both forms
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "first_name":
      case "last_name":
        if (!value.trim()) {
          error = "This field is required";
        } else if (!validateName(value.trim())) {
          error =
            "Name must contain only letters (a-z, A-Z) and be 1-20 characters long";
        } else if (value.includes(" ")) {
          error = `${name.replace("_", " ")} should not contain spaces`;
        } else if (!/^[a-zA-Z-]+$/.test(value)) {
          error = `${name.replace("_", " ")} should only contain letters`;
        }
        break;

      case "password":
        if (!value) {
          error = "Password is required";
        } else if (value.length < 8) {
          error = "Password must be at least 8 characters long";
        } else if (value.length > 30) {
          error = "Password must be less than 30 characters";
        } else if (!validatePassword(value)) {
          error = "Password cannot contain spaces";
        }
        break;

      case "phone":
        const digitsOnly = value.replace(/\D/g, "");
        if (!digitsOnly) {
          error = "Phone number is required";
        } else if (!validatePhone(digitsOnly)) {
          error = "Phone number must be in the format 03XXXXXXXXX";
        }
        break;

      case "email":
        if (!value.trim()) {
          error = "Email is required";
        } else if (!validateEmail(value.trim())) {
          error = "Please enter a valid email address";
        }
        break;

      default:
        break;
    }

    return error;
  };

  // Handle input change for user form
  const handleChange = (e) => {
    const { name, value } = e.target;

    // For phone field, format the input
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "");
      setCredentials({ ...credentials, [name]: digitsOnly });

      // Update error for phone
      if (touched[name] || errors[name]) {
        const error = validateField(name, digitsOnly);
        setErrors((prev) => ({ ...prev, [name]: error }));
      }
      return;
    }

    // For all other fields, just update the value
    setCredentials({ ...credentials, [name]: value });

    // Update error if field has been touched or already has an error
    if (touched[name] || errors[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  // Handle input change for chef form
  const handleChefChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "");
      setChefFormData({ ...chefFormData, [name]: digitsOnly });

      const error = validateField(name, digitsOnly);
      setChefErrors((prev) => ({ ...prev, [name]: error }));
      return;
    }

    setChefFormData({ ...chefFormData, [name]: value });

    const error = validateField(name, value);
    setChefErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Handle blur event for user form
  const handleBlur = (e) => {
    const { name, value } = e.target;

    setTouched((prev) => ({ ...prev, [name]: true }));

    let trimmedValue = value;
    if (name === "first_name" || name === "last_name" || name === "email") {
      trimmedValue = value.trim();
      setCredentials((prev) => ({ ...prev, [name]: trimmedValue }));
    }

    const error = validateField(name, trimmedValue || value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Handle blur event for chef form
  const handleChefBlur = (e) => {
    const { name, value } = e.target;

    let trimmedValue = value;
    if (name === "first_name" || name === "last_name" || name === "email") {
      trimmedValue = value.trim();
      setChefFormData((prev) => ({ ...prev, [name]: trimmedValue }));
    }

    const error = validateField(name, trimmedValue || value);
    setChefErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Chef image handlers
  const handleChefProfileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setChefFormData((prev) => ({ ...prev, profile_pic: file }));
      const reader = new FileReader();
      reader.onload = () => {
        setIsProfile(reader.result);
      };
      reader.readAsDataURL(file);
      setChefErrors((prev) => ({ ...prev, profile_pic: "" }));
    }
  };

  const handleChefCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setChefFormData((prev) => ({ ...prev, cover_pic: file }));
      const reader = new FileReader();
      reader.onload = () => {
        setIsCover(reader.result);
      };
      reader.readAsDataURL(file);
      setChefErrors((prev) => ({ ...prev, cover_pic: "" }));
    }
  };

  const handleChefNicImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setChefFormData((prev) => ({ ...prev, nic_image: file }));
      const reader = new FileReader();
      reader.onload = () => {
        setIsNicImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChefLatestBillChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setChefFormData((prev) => ({ ...prev, latest_bill: file }));
      const reader = new FileReader();
      reader.onload = () => {
        setIsLatestBill(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleChefBoxClick = () => {
    if (chefFileInputRef.current) {
      chefFileInputRef.current.click();
    }
  };

  // Check if user form is valid
  const isUserFormValid = () => {
    return (
      !errors.first_name &&
      !errors.last_name &&
      !errors.email &&
      !errors.password &&
      !errors.phone &&
      credentials.first_name.trim() &&
      credentials.last_name.trim() &&
      credentials.email.trim() &&
      credentials.password &&
      credentials.phone.replace(/\D/g, "").length === 11
    );
  };

  // Check if chef form is valid
  const isChefFormValid = () => {
    return (
      !chefErrors.first_name &&
      !chefErrors.last_name &&
      !chefErrors.email &&
      !chefErrors.password &&
      !chefErrors.phone &&
      !chefErrors.nic_number &&
      !chefErrors.profile_pic &&
      !chefErrors.cover_pic &&
      chefFormData.first_name.trim() &&
      chefFormData.last_name.trim() &&
      chefFormData.email.trim() &&
      chefFormData.password &&
      chefFormData.phone.replace(/\D/g, "").length === 11 &&
      chefFormData.nic_number.trim() &&
      chefFormData.profile_pic &&
      chefFormData.cover_pic &&
      selectedCity &&
      selectedArea &&
      streetAddress.trim() &&
      isChefLocationConfirmed &&
      chefCoordinates.lat &&
      chefCoordinates.lng &&
      chefTermsAgreed &&
      chefAcknowledgementAgreed
    );
  };

  // 🔹 Handle resend email verification
  const handleResendEmail = async () => {
    try {
      const res = await handleResendEmailVerificationLink(resendEmail);
      toast.success(res.message || "Verification email resent!");
    } catch (err) {
      toast.error(err.message || "Failed to resend email.");
    }
  };

  // User form submission
  const handleUserSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      first_name: validateField("first_name", credentials.first_name),
      last_name: validateField("last_name", credentials.last_name),
      email: validateField("email", credentials.email),
      password: validateField("password", credentials.password),
      phone: validateField("phone", credentials.phone),
    };

    setErrors(newErrors);
    setTouched({
      email: true,
      password: true,
      phone: true,
      first_name: true,
      last_name: true,
    });

    const hasErrors = Object.values(newErrors).some((error) => error !== "");
    if (hasErrors) {
      toast.error("Please fix all errors before submitting");
      return;
    }

    if (isUserFormValid()) {
      setIsLoading(true);
      try {
        const submitData = {
          ...credentials,
          email: credentials.email.trim(),
          first_name: credentials.first_name.trim(),
          last_name: credentials.last_name.trim(),
        };

        const res = await handleUserSignUp(submitData);
        const successMessage =
          typeof res.message === "string"
            ? res.message
            : res.message.original.message || "Registered Successfully";

        toast.success(successMessage);

        // ⏳ wait few seconds then redirect
        setTimeout(() => {
          navigate("/login");
        }, 2500); // adjust delay if needed

        setCredentials({
          email: "",
          password: "",
          phone: "",
          first_name: "",
          last_name: "",
        });

        setErrors({
          email: "",
          password: "",
          phone: "",
          first_name: "",
          last_name: "",
        });
        setTouched({
          email: false,
          password: false,
          phone: false,
          first_name: false,
          last_name: false,
        });

        if (res.success && res.requiresVerification) {
          setResendEmail(credentials.email);
          setShowResendButton(true);
        } else {
          setShowResendButton(false);
        }

        setIsLoading(false);
      } catch (error) {
        toast.error(error.message);
        setIsLoading(false);
      }
    }
  };

  // Chef form submission
  const handleChefSubmit = async (e) => {
    e.preventDefault();

    // Validate all chef fields
    const newChefErrors = {
      first_name: validateField("first_name", chefFormData.first_name),
      last_name: validateField("last_name", chefFormData.last_name),
      email: validateField("email", chefFormData.email),
      password: validateField("password", chefFormData.password),
      phone: validateField("phone", chefFormData.phone),
      nic_number: !chefFormData.nic_number.trim()
        ? "NIC number is required"
        : "",
      profile_pic: !chefFormData.profile_pic
        ? "Profile picture is required"
        : "",
      cover_pic: !chefFormData.cover_pic ? "Cover photo is required" : "",
    };

    setChefErrors(newChefErrors);

    // Validate checkboxes
    if (!chefTermsAgreed) {
      toast.error("Please agree to the Terms of Service");
      return;
    }
    if (!chefAcknowledgementAgreed) {
      toast.error("Please acknowledge the independent business agreement");
      return;
    }

    // Validate city, area, and street address
    if (!selectedCity) {
      toast.error("Please select a city");
      return;
    }
    if (!selectedArea) {
      toast.error("Please select an area");
      return;
    }
    if (!streetAddress.trim()) {
      toast.error("Please enter your street address");
      return;
    }
    if (!isChefLocationConfirmed || !chefCoordinates.lat || !chefCoordinates.lng) {
      toast.error("Please confirm your location on the map");
      return;
    }

    const hasErrors = Object.values(newChefErrors).some(
      (error) => error !== "",
    );
    if (hasErrors) {
      toast.error("Please fix all errors before submitting");
      return;
    }

    if (isChefFormValid()) {
      setChefLoading(true);
      try {
        // Create FormData for file uploads
        const submissionFormData = new FormData();

        // Add all chef form data
        submissionFormData.append("first_name", chefFormData.first_name.trim());
        submissionFormData.append("last_name", chefFormData.last_name.trim());
        submissionFormData.append("email", chefFormData.email.trim());
        submissionFormData.append("password", chefFormData.password);
        submissionFormData.append("phone", chefFormData.phone);
        submissionFormData.append("is_chef", "1");
        submissionFormData.append("nic_number", chefFormData.nic_number);
        submissionFormData.append("address", `${selectedCity}, ${selectedArea}, ${streetAddress.trim()}`);
        submissionFormData.append("latitude", chefCoordinates.lat);
        submissionFormData.append("longitude", chefCoordinates.lng);

        // Add files if they exist
        if (chefFormData.profile_pic) {
          submissionFormData.append("profile_pic", chefFormData.profile_pic);
        }
        if (chefFormData.cover_pic) {
          submissionFormData.append("cover_pic", chefFormData.cover_pic);
        }
        if (chefFormData.nic_image) {
          submissionFormData.append("nic_image", chefFormData.nic_image);
        }
        if (chefFormData.latest_bill) {
          submissionFormData.append("latest_bill", chefFormData.latest_bill);
        }

        const res = await handleChefSignUp(submissionFormData);
        const successMessage =
          typeof res.message === "string"
            ? res.message
            : res.message.original.message || "Registered Successfully";
        toast.success(successMessage);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "chef_signup",
        });

        setTimeout(() => {
          navigate("/login");
        }, 2500);

        // Reset chef form
        setChefFormData({
          first_name: "",
          last_name: "",
          email: "",
          password: "",
          phone: "",
          profile_pic: "",
          cover_pic: "",
          is_chef: 1,
          nic_number: "",
          nic_image: "",
          latest_bill: "",
        });

        setChefErrors({
          email: "",
          password: "",
          phone: "",
          first_name: "",
          last_name: "",
          profile_pic: "",
          cover_pic: "",
          nic_number: "",
        });

        setIsProfile("");
        setIsCover("");
        setIsNicImage("");
        setIsLatestBill("");
        setChefTermsAgreed(false);
        setChefAcknowledgementAgreed(false);
        setSelectedCity("");
        setSelectedArea("");
        setStreetAddress("");
        setAreas([]);
        setChefCoordinates({ lat: "", lng: "" });
        setIsChefLocationConfirmed(false);

        setChefLoading(false);
      } catch (error) {
        toast.error(
          error.message || "An error occurred during chef registration",
        );
        setChefLoading(false);
      }
    }
  };

  // Map callbacks for chef form
  const handleChefPlaceSelect = ({ lat, lng }) => {
    setChefCoordinates({ lat, lng });
  };

  const handleChefReverseGeocode = (formattedAddress) => {
    // Address from map is available if needed; we use city/area/street instead
  };

  const handleChefDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setChefCoordinates({ lat, lng });
  };

  const handleChefLocationConfirmed = (confirmed) => {
    setIsChefLocationConfirmed(confirmed);
  };

  const fadeInUp = {
    initial: { opacity: 0, y: 40 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.8 } },
  };
  const stagger = { animate: { transition: { staggerChildren: 0.15 } } };

  // SVG Icons
  const UserIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );

  const ChefHatIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
      />
    </svg>
  );

  const EyeIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
      />
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
      />
    </svg>
  );

  const EyeSlashIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L6.59 6.59m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
      />
    </svg>
  );

  return (
    <section className="min-h-screen py-4 flex items-center overflow-hidden bg-black relative">
      {/* BG IMAGE + OVERLAY - Absolute positioning within section */}
      <div className="absolute inset-0 z-0">
        <img
          src="/media/frontend/img/banner/hero-banner.jpg"
          alt="food"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/50 to-transparent" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.div
          initial="initial"
          animate="animate"
          variants={stagger}
          className="grid lg:grid-cols-2 gap-16 items-center"
        >
          {/* LEFT: TEXT */}
          <motion.div variants={fadeInUp} className="text-white space-y-6">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-tight text-white">
              From their kitchen, <br />
              <span className="bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                to your table.
              </span>
            </h1>
            <div className="w-32 h-1 bg-red-600 rounded-full mt-3 mx-auto lg:mx-0"></div>
            <p className="text-lg md:text-xl text-gray-200 max-w-lg leading-relaxed italic">
              Experience the{" "}
              <span
                style={{
                  color: "white",
                  textShadow:
                    "0 0 8px #f43f5e, 0 0 12px #f43f5e, 0 0 16px #f43f5e",
                }}
              >
                warmth
              </span>{" "}
              of home-cooked meals. Discover authentic dishes crafted by
              passionate local chefs and delivered to you.
            </p>
          </motion.div>

          {/* RIGHT: FORM */}
          <motion.div variants={fadeInUp}>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 md:p-8 border border-white/20 shadow-2xl">
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-white text-center mb-1">
                  {activeTab === "user" ? "Get Started Now" : "Become a Chef"}
                </h3>
                <p className="text-gray-300 text-center mb-4">
                  {activeTab === "user"
                    ? "Create your account to start ordering."
                    : "Start your culinary journey and share your passion."}
                </p>

                {/* Tab Selector */}
                <div className="flex space-x-2 mb-6">
                  <button
                    type="button"
                    onClick={() => setActiveTab("user")}
                    className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all ${
                      activeTab === "user"
                        ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg"
                        : "bg-white/5 text-gray-300 hover:bg-white/10"
                    }`}
                  >
                    <UserIcon />
                    <span className="font-medium">Food Lover</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveTab("chef")}
                    className={`flex-1 py-3 px-4 rounded-lg flex items-center justify-center gap-2 transition-all ${
                      activeTab === "chef"
                        ? "bg-gradient-to-r from-red-600 to-red-700 text-white shadow-lg"
                        : "bg-white/5 text-gray-300 hover:bg-white/10"
                    }`}
                  >
                    <ChefHatIcon />
                    <span className="font-medium">Become a Chef</span>
                  </button>
                </div>
              </div>

              {activeTab === "user" ? (
                /* USER FORM */
                <form onSubmit={handleUserSubmit} className="space-y-4">
                  {/* Email Field */}
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={credentials.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        errors.email ? "border-red-500" : "border-white/30"
                      }`}
                      required
                    />
                    {errors.email && (
                      <p className="text-sm text-red-300 mt-1">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* First Name Field */}
                  <div>
                    <input
                      type="text"
                      name="first_name"
                      placeholder="First Name"
                      value={credentials.first_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        errors.first_name ? "border-red-500" : "border-white/30"
                      }`}
                      required
                    />
                    {errors.first_name && (
                      <p className="text-sm text-red-300 mt-1">
                        {errors.first_name}
                      </p>
                    )}
                  </div>

                  {/* Last Name Field */}
                  <div>
                    <input
                      type="text"
                      name="last_name"
                      placeholder="Last Name"
                      value={credentials.last_name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        errors.last_name ? "border-red-500" : "border-white/30"
                      }`}
                      required
                    />
                    {errors.last_name && (
                      <p className="text-sm text-red-300 mt-1">
                        {errors.last_name}
                      </p>
                    )}
                  </div>

                  {/* Phone Field */}
                  <div>
                    <input
                      type="text"
                      name="phone"
                      value={credentials.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="03XXXXXXXXX"
                      maxLength={11}
                      pattern="^03[0-9]{9}$"
                      className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        errors.phone ? "border-red-500" : "border-white/30"
                      }`}
                      required
                    />
                    {errors.phone && (
                      <p className="text-sm text-red-300 mt-1">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* Password Field */}
                  <div>
                    <div className="relative">
                      <input
                        value={credentials.password}
                        name="password"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        required
                        type={showPassword ? "text" : "password"}
                        placeholder="Password (min 8 characters)"
                        maxLength={30}
                        minLength={8}
                        className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                          errors.password ? "border-red-500" : "border-white/30"
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <button
                          type="button"
                          onClick={() => setShowPassword((prev) => !prev)}
                          className="text-gray-300 hover:text-white focus:outline-none transition-colors"
                          tabIndex={-1}
                        >
                          {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                      </div>
                    </div>
                    {errors.password && (
                      <p className="text-sm text-red-300 mt-1">
                        {errors.password}
                      </p>
                    )}
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={!isUserFormValid() || isLoading}
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-red-600/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="w-5 h-5 mr-2 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 100 8v4a8 8 0 01-8-8z"
                          ></path>
                        </svg>
                        Creating Account...
                      </span>
                    ) : (
                      "Create Account"
                    )}
                  </motion.button>

                  {/* 🔹 Resend Email Button */}
                  {showResendButton && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-center mt-3"
                    >
                      <button
                        type="button"
                        onClick={handleResendEmail}
                        className="text-sm font-medium bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent hover:opacity-80 transition-all"
                      >
                        Resend Verification Email
                      </button>
                    </motion.div>
                  )}
                </form>
              ) : (
                /* CHEF FORM - Similar to TopBanner */
                <form onSubmit={handleChefSubmit} className="space-y-4">
                  <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                    <p className="text-red-200 text-sm text-center">
                      Complete your profile to start your culinary journey!
                    </p>
                  </div>

                  {/* Image Upload Section */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Profile Picture */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-white">
                        Profile Picture *
                      </label>
                      <div
                        ref={chefProfileDiv}
                        className="w-full h-40 border-2 border-dashed border-white/30 rounded-lg overflow-hidden relative cursor-pointer hover:border-red-500 transition-colors"
                        onClick={handleChefBoxClick}
                      >
                        {isProfile ? (
                          <img
                            src={isProfile}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-4">
                            <svg
                              className="w-12 h-12 text-gray-400 mb-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                              />
                            </svg>
                            <p className="text-center text-sm text-gray-300">
                              Click to upload profile
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              500x500px recommended
                            </p>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        name="profile_pic"
                        accept="image/*"
                        onChange={handleChefProfileChange}
                        ref={chefFileInputRef}
                        style={{ display: "none" }}
                      />
                      {chefErrors.profile_pic && (
                        <p className="text-sm text-red-300">
                          {chefErrors.profile_pic}
                        </p>
                      )}
                    </div>

                    {/* Cover Photo */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-white">
                        Cover Photo *
                      </label>
                      <div
                        className="w-full h-40 border-2 border-dashed border-white/30 rounded-lg overflow-hidden relative cursor-pointer hover:border-red-500 transition-colors"
                        onClick={() => chefCoverImageRef.current.click()}
                      >
                        {isCover ? (
                          <img
                            src={isCover}
                            alt="Cover"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center p-4">
                            <svg
                              className="w-12 h-12 text-gray-400 mb-2"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            <p className="text-center text-sm text-gray-300">
                              Click to upload cover
                            </p>
                            <p className="text-xs text-gray-400 mt-1 text-center">
                              1200×400px — horizontal banner for your chef profile
                            </p>
                          </div>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleChefCoverImageChange}
                        ref={chefCoverImageRef}
                        style={{ display: "none" }}
                      />
                      {chefErrors.cover_pic && (
                        <p className="text-sm text-red-300">
                          {chefErrors.cover_pic}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Personal Information Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <input
                        type="text"
                        name="first_name"
                        placeholder="First Name"
                        value={chefFormData.first_name}
                        onChange={handleChefChange}
                        onBlur={handleChefBlur}
                        className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                          chefErrors.first_name
                            ? "border-red-500"
                            : "border-white/30"
                        }`}
                        required
                      />
                      {chefErrors.first_name && (
                        <p className="text-sm text-red-300 mt-1">
                          {chefErrors.first_name}
                        </p>
                      )}
                    </div>

                    <div>
                      <input
                        type="text"
                        name="last_name"
                        placeholder="Last Name"
                        value={chefFormData.last_name}
                        onChange={handleChefChange}
                        onBlur={handleChefBlur}
                        className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                          chefErrors.last_name
                            ? "border-red-500"
                            : "border-white/30"
                        }`}
                        required
                      />
                      {chefErrors.last_name && (
                        <p className="text-sm text-red-300 mt-1">
                          {chefErrors.last_name}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={chefFormData.email}
                      onChange={handleChefChange}
                      onBlur={handleChefBlur}
                      className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        chefErrors.email ? "border-red-500" : "border-white/30"
                      }`}
                      required
                    />
                    {chefErrors.email && (
                      <p className="text-sm text-red-300 mt-1">
                        {chefErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <input
                        value={chefFormData.password}
                        name="password"
                        onChange={handleChefChange}
                        onBlur={handleChefBlur}
                        required
                        type={showChefPassword ? "text" : "password"}
                        placeholder="Password (min 8 characters)"
                        maxLength={30}
                        minLength={8}
                        className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                          chefErrors.password
                            ? "border-red-500"
                            : "border-white/30"
                        }`}
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-4">
                        <button
                          type="button"
                          onClick={() => setShowChefPassword((prev) => !prev)}
                          className="text-gray-300 hover:text-white focus:outline-none transition-colors"
                          tabIndex={-1}
                        >
                          {showChefPassword ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                      </div>
                    </div>
                    {chefErrors.password && (
                      <p className="text-sm text-red-300 mt-1">
                        {chefErrors.password}
                      </p>
                    )}
                  </div>

                  <div>
                    <input
                      type="text"
                      name="phone"
                      value={chefFormData.phone}
                      onChange={handleChefChange}
                      onBlur={handleChefBlur}
                      placeholder="03XXXXXXXXX"
                      maxLength={11}
                      pattern="^03[0-9]{9}$"
                      className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        chefErrors.phone
                          ? "border-red-500"
                          : "border-white/30"
                      }`}
                      required
                    />
                    {chefErrors.phone && (
                      <p className="text-sm text-red-300 mt-1">
                        {chefErrors.phone}
                      </p>
                    )}
                  </div>

                  {/* NIC Number Field */}
                  <div>
                    <input
                      type="text"
                      name="nic_number"
                      placeholder="NIC Number (e.g., 12345-1234567-1)"
                      value={chefFormData.nic_number}
                      onChange={handleChefChange}
                      onBlur={handleChefBlur}
                      maxLength={20}
                      className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        chefErrors.nic_number
                          ? "border-red-500"
                          : "border-white/30"
                      }`}
                      required
                    />
                    {chefErrors.nic_number && (
                      <p className="text-sm text-red-300 mt-1">
                        {chefErrors.nic_number}
                      </p>
                    )}
                  </div>

                  {/* City Selector */}
                  <div>
                    <select
                      value={selectedCity}
                      onChange={(e) => {
                        setSelectedCity(e.target.value);
                        setSelectedArea("");
                      }}
                      className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        !selectedCity ? "border-red-500" : "border-white/30"
                      }`}
                      required
                    >
                      <option value="" className="text-black">
                        Select City *
                      </option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.name} className="text-black">
                          {city.name}
                        </option>
                      ))}
                    </select>
                    {!selectedCity && (
                      <p className="text-sm text-red-300 mt-1">City is required</p>
                    )}
                  </div>

                  {/* Area Selector */}
                  <div>
                    <SearchableSelect
                      options={areas}
                      value={selectedArea}
                      onChange={setSelectedArea}
                      placeholder={selectedCity ? "Select Area *" : "Select a city first"}
                      disabled={!selectedCity}
                      required
                      variant="dark"
                      className={`px-4 py-3 bg-white/20 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        !selectedArea ? "border-red-500" : "border-white/30"
                      }`}
                    />
                    {!selectedArea && selectedCity && (
                      <p className="text-sm text-red-300 mt-1">Area is required</p>
                    )}
                  </div>

                  {/* Street Address */}
                  <div>
                    <input
                      type="text"
                      name="street_address"
                      placeholder="Street Address (e.g., House #, Block, Road) *"
                      value={streetAddress}
                      onChange={(e) => setStreetAddress(e.target.value)}
                      className={`w-full px-4 py-3 bg-white/20 border rounded-lg text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-red-600 transition-all ${
                        !streetAddress.trim() ? "border-red-500" : "border-white/30"
                      }`}
                      required
                    />
                    {!streetAddress.trim() && (
                      <p className="text-sm text-red-300 mt-1">Street address is required</p>
                    )}
                  </div>

                  {/* Map Location Picker */}
                  <div className="bg-white/10 rounded-lg p-3">
                    <label className="block text-sm font-medium text-white mb-2">
                      Confirm Your Location on Map *
                    </label>
                    <MapWithConditionalRendering
                      coordinates={chefCoordinates}
                      onDragEnd={handleChefDragEnd}
                      showMap={true}
                      onReverseGeocode={handleChefReverseGeocode}
                      onPlaceSelect={handleChefPlaceSelect}
                      onLocationConfirmed={handleChefLocationConfirmed}
                    />
                    {!isChefLocationConfirmed && (
                      <p className="text-sm text-red-300 mt-1">
                        Please search a landmark or drag the pin to confirm your location
                      </p>
                    )}
                  </div>

                  {/* NIC Image Upload */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-white">
                      NIC Image (Optional)
                    </label>
                    <div
                      className="w-full h-32 border-2 border-dashed border-white/30 rounded-lg overflow-hidden relative cursor-pointer hover:border-red-500 transition-colors"
                      onClick={() => chefNicImageRef.current.click()}
                    >
                      {isNicImage ? (
                        <img
                          src={isNicImage}
                          alt="NIC"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center p-4">
                          <svg
                            className="w-10 h-10 text-gray-400 mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <p className="text-center text-sm text-gray-300">
                            Click to upload NIC image
                          </p>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleChefNicImageChange}
                      ref={chefNicImageRef}
                      style={{ display: "none" }}
                    />
                  </div>

                  {/* Latest Bill Upload */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-white">
                      Latest Bill (Optional)
                    </label>
                    <p className="text-xs text-gray-400">
                      To verify NIC address
                    </p>
                    <div
                      className="w-full h-32 border-2 border-dashed border-white/30 rounded-lg overflow-hidden relative cursor-pointer hover:border-red-500 transition-colors"
                      onClick={() => chefLatestBillRef.current.click()}
                    >
                      {isLatestBill ? (
                        <img
                          src={isLatestBill}
                          alt="Latest Bill"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex flex-col items-center justify-center p-4">
                          <svg
                            className="w-10 h-10 text-gray-400 mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          <p className="text-center text-sm text-gray-300">
                            Click to upload latest bill
                          </p>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      onChange={handleChefLatestBillChange}
                      ref={chefLatestBillRef}
                      style={{ display: "none" }}
                    />
                  </div>

                  {/* Chef Terms - Two Checkboxes like TopBanner */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-start">
                      <div className="flex items-center h-5 mt-0.5">
                        <input
                          id="chef-terms"
                          type="checkbox"
                          checked={chefTermsAgreed}
                          onChange={(e) => setChefTermsAgreed(e.target.checked)}
                          className="w-4 h-4 text-red-600 bg-white/20 border-white/30 rounded focus:ring-red-600 focus:ring-2"
                          required
                        />
                      </div>
                      <label
                        htmlFor="chef-terms"
                        className="ml-3 text-sm text-white"
                      >
                        I agree to Shef's{" "}
                        <Link
                          to="/terms-of-services"
                          className="text-red-300 hover:text-red-200 underline"
                        >
                          Terms of Service
                        </Link>
                        , and to receive marketing text messages.
                      </label>
                    </div>

                    <div className="flex items-start">
                      <div className="flex items-center h-5 mt-0.5">
                        <input
                          id="chef-acknowledgement"
                          type="checkbox"
                          checked={chefAcknowledgementAgreed}
                          onChange={(e) =>
                            setChefAcknowledgementAgreed(e.target.checked)
                          }
                          className="w-4 h-4 text-red-600 bg-white/20 border-white/30 rounded focus:ring-red-600 focus:ring-2"
                          required
                        />
                      </div>
                      <label
                        htmlFor="chef-acknowledgement"
                        className="ml-3 text-sm text-white"
                      >
                        I acknowledge and agree that I am an independent
                        business and authorized to earn income in Pakistan.
                      </label>
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={!isChefFormValid() || chefLoading}
                    className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white font-bold py-3 px-6 rounded-lg shadow-lg hover:shadow-red-600/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {chefLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="w-5 h-5 mr-2 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 100 8v4a8 8 0 01-8-8z"
                          ></path>
                        </svg>
                        Creating Chef Account...
                      </span>
                    ) : (
                      "Become a Chef"
                    )}
                  </motion.button>
                </form>
              )}

              {/* Switch Form CTA */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="mt-6 text-center"
              >
                <button
                  type="button"
                  onClick={() => {
                    setActiveTab(activeTab === "user" ? "chef" : "user");
                    // Reset form errors when switching
                    if (activeTab === "user") {
                      setChefErrors({
                        email: "",
                        password: "",
                        phone: "",
                        first_name: "",
                        last_name: "",
                        profile_pic: "",
                        cover_pic: "",
                      });
                    } else {
                      setErrors({
                        email: "",
                        password: "",
                        phone: "",
                        first_name: "",
                        last_name: "",
                      });
                    }
                  }}
                  className="text-sm font-medium text-gray-300 hover:text-white transition-all"
                >
                  {/* {activeTab === "user" 
                    ? "Want to become a chef instead?" 
                    : "Just want to order food?"}{" "}
                  <span className="underline underline-offset-4">
                    {activeTab === "user" ? "Become a chef" : "Create user account"}
                  </span> */}
                </button>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroBanner;
